<template>
  <div id="estimation" class="pt-2 pb-5">

    <div v-if="!firstLoad" class="estimation__info columns is-multiline">

      <div
        v-if="(!estimation.rfq.is_accepted || 
            !estimation.rfq.drawing_highlights[0].is_approved) && 
            !estimation.is_completed" 
        class="column is-full">
        <v-alert 
          type="danger">
          <span v-if="!estimation.rfq.is_accepted">
            This estimation cannot be started because the RFQ has not been
            approved by an estimator.
          </span>
          <span v-else>
            This estimation cannot be started because the 
            drawing highlight has not been approved.
          </span>
        </v-alert>
      </div>

      <div class="column is-8 flex flex--row flex--align-start px-4">
        <div class="columns is-fullwidth">
          <div class="column is-4">
            <label class="help">Project</label>
            <div class="subtitle is-6 py-2">
              {{ estimation.rfq.project.project_name }}
            </div>
          </div>

          <div class="column is-3">
            <label class="help">RFQ</label>
            <div class="subtitle is-6 py-2">
              <router-link 
                :to="{ 
                  name: 'RfqViewer', 
                  params: { 
                    rfqId: estimation.rfq.id,
                    productBrand: 'Cladify'
                  } 
                }">
                {{ estimation.rfq.rfq_name }}
              </router-link>
            </div>
          </div>

          <div class="column is-3">
            <label class="help text--nowrap">Estimation Status</label>
            <div class="subtitle is-6 py-2 is-capitalized">
              {{ estimation.status.toLowerCase() }}
            </div>
          </div>

          <div class="column is-3">
            <label class="help">Main Estimator</label>
            <v-select
              class="mt-1"
              v-if="users.length > 0"
              required
              is-small
              item-text="display_name"
              item-value="id"
              :options="users.filter(d => ['estimator', 'sales-estimator', 'sales-estimator-samples'].includes(d.role))"
              :disabled="!editable"
              v-model="estimation.rfq.main_estimator_id"
              @input="changeEstimator($event)"
            />
          </div>

        </div>
      </div>

      <div 
        class="column flex flex--align-start buttons flex--justify-end pt-4">

        <button
          v-if="!firstLoad && 
            !estimation.is_ready_for_pricing && 
            can.markAsReadyForPricing"
          @click="confirmReadyForPricing()"
          class="button is-primary"
          :disabled="!editable">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Mark as Ready for Pricing
          </span>
        </button>

        <!-- <button
          v-if="!firstLoad && estimation.is_completed && !estimation.is_director_approved"
          @click="confirmDirectorApproval()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Approved by Director
          </span>
        </button> -->

        <button
          v-if="
            !firstLoad && 
            estimation.is_ready_for_pricing && 
            !estimation.is_completed && can.markAsCompleted"
          @click="confirmCompleted()"
          class="button is-primary"
          :disabled="!editable">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Mark as Complete
          </span>
        </button>

        <button
          v-if="
            !firstLoad && 
            estimation.is_ready_for_pricing &&
            estimation.is_completed && 
            !estimation.is_client_approved"
          @click="duplicatingRfq = true"
          class="button">
          <span class="icon is-medium">
            <i class="fas fa-copy"></i>
          </span>
          <span>              
            Create New Version
          </span>
        </button>

        <button
          v-if="active == 'detailed_summary' && editable && 
            estimation.rfq.rfq_type !== CONDENSED"
          @click="bulkAdjustingPrice = true"
          class="button"
          :disabled="!editable">
          <span class="icon is-medium">
            <i class="fas fa-edit"></i>
          </span>
          <span>              
            Bulk Adjust Item Prices
          </span>
        </button>

        <!-- <button
          v-if="
            !firstLoad && 
            estimation.is_ready_for_pricing &&
            estimation.is_completed && 
            !estimation.is_client_approved"
          @click="confirmClientApproval()"
          class="button is-primary">
          <span class="icon is-medium">
            <i class="fas fa-check"></i>
          </span>
          <span>              
            Approved
          </span>
        </button> -->

        <v-fade-transition mode="out-in">
          <button 
            v-if="!firstLoad && !estimation.is_ready_for_pricing 
              // && active == 'estimation_data'
              && estimation.rfq.rfq_type !== CONDENSED"
            class="button is-link" 
            :class="{ 'is-loading': saving }"
            :disabled="saving || !editable"
            @click.stop="saveChanges()">
            <span class="icon is-medium">
              <i class="fas fa-save"></i>
            </span>
            <span>              
              Save and Refresh
            </span>
          </button>
        </v-fade-transition>
        
      </div>           
    </div>

    <estimation-condensed
      v-if="estimation && estimation.rfq.rfq_type === CONDENSED"
      ref="condensedEstimation"
      :editable="editable"
    />

    <estimation-full-cladify
      v-if="estimation && estimation.rfq.rfq_type !== CONDENSED"
      :editable="editable"
      :adjustPrice="adjustPrice"
      :can="can"
      :setSaving="setSaving"
      :active="active"
      :setActive="setActive"
      ref="fullEstimation"
    />

    <v-confirm-modal
      v-model="confirmModal.state"
      :title="confirmModal.title"
      :type="confirmModal.type"
      :message="confirmModal.message"
      :pending="confirmModal.pending"
      @confirm="confirmModal.confirmCb"
    />

    <duplicate-rfq-modal
      v-if="!firstLoad"
      v-model="duplicatingRfq"
      :rfq="estimation.rfq"
      :duplicate-estimation="true"
    />

    <v-loading 
      key="cladifyEstimation"
      :value="firstLoad || blockSaving || saving || savingSummary"
      :message="loadingMessage"
    />

  </div>
</template>

<script>
import VConfirmModal from '@/components/base/VConfirmModal'
import VSelect from '@/components/base/VSelect'
import VLoading from '@/components/base/VLoading'
import VAlert from '@/components/base/VAlert'
import VFadeTransition from '@/components/transitions/VFadeTransition'
import DuplicateRfqModal from '@/components/rfq/DuplicateRfqModal'

import EstimationFullCladify from '@/components/estimations/EstimationFullCladify'
import EstimationCondensed from '@/components/estimations/EstimationCondensed'

import { CONDENSED, FULL } from '@/helpers/constants'
import { commonEstimationHelpers } from '@/mixins/helpers'
import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: {
    VSelect, VConfirmModal, VLoading, VAlert, VFadeTransition,
    DuplicateRfqModal, EstimationFullCladify, EstimationCondensed
  },
  mixins: [commonEstimationHelpers],
  data() {
    return {
      CONDENSED: CONDENSED,
      FULL: FULL,
      saving: false,
      savingSummary: false,
      duplicatingRfq: false,
      confirmModal: {
        state: false,
        title: 'Confirm mark as complete',
        message: 'Are you sure you want to mark this estimation as complete? \
        Once marked as complete, you will not be able make more edits \
        unless a revision is requested ',
        type: 'warning',
        pending: false,
        confirmCb: () => {}
      },
      active: 'estimation_data'
    }
  },
  computed: {
    ...mapState("cladifyEstimation", [
      "estimation", "loading", "firstLoad", "blockSaving"
    ]),
    ...mapState("users", ["users"]),
    ...mapState("auth", ["user"]),
    can({ user }) {
      return {
        markAsReadyForPricing: ["ceo", "estimator", "sales-estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin',
        markAsCompleted: ["ceo", "sales", "sales-estimator", "sales-estimator-samples"].includes(user.role) || user.permission == 'admin'
      }
    },
    editable({ estimation }) {
      return estimation.rfq.is_accepted && 
        estimation.rfq.drawing_highlights[0] &&
        estimation.rfq.drawing_highlights[0].is_approved && 
        !this.estimation.is_completed
    },
    loadingMessage({ blockSaving, savingSummary, saving }) {
      if (blockSaving) {
        return "Hold your horses, we're recalculating the estimation ..."
      } else if (savingSummary) {
        return "Just a second, we're updating the estimation ..."
      } else if (saving) {
        return "Saving estimation details and recalculating summaries ..."
      } else {
        return "Loading estimation, this may take a few seconds ..."
      }
    }
  },
  watch: {
    '$route.params.estimationId'(id) {
      this.initialize();
    }
  },
  mounted() {
    this.initialize();
  },
  methods: {
    ...mapActions("cladifyEstimation", [
      "getEstimation", "updateEstimation", "updateEstimator"
    ]),
    ...mapMutations("cladifyEstimation", [
      "setLoading", "setFirstLoad", "setEstimation"
    ]),
    ...mapActions("users", ["getUsers"]),
    initialize() {
      let estimation = this.$route.params.estimation;
      if (estimation) {
        this.setEstimation(estimation);
        this.setUpEstimation();
        this.setLoading(false);
        this.setFirstLoad(false);
      } else {
        this.getEstimation(this.$route.params.estimationId).then(() => {
          this.setUpEstimation();
        });
      }

      this.getUsers({ per_page: 100 });
    },
    setUpEstimation() {
      if (this.estimation.rfq.rfq_type !== CONDENSED) {
        this.$refs.fullEstimation.initializeEstimation();
      } else {
        this.$refs.condensedEstimation.initializeEstimation();
      }
    },
    setActive(v) {
      this.active = v;
    },
    changeEstimator($event) {
      this.updateEstimator({
        rfqId: this.estimation.rfq.id,
        data: { main_estimator_id: $event }
      })
    },
    confirmReadyForPricing() {
      const workingFromDraft = this.estimation.rfq.rfq_type !== CONDENSED
        ? this.$refs.fullEstimation.workingFromDraft
        : false

      this.confirmModal.confirmCb = () => {
        this.confirmModal.pending = true;
        if (workingFromDraft) {
          this.saveChanges().then(() => {
            this.confirmModal.pending = false;
            this.markAsReadyForPricing();
          }).catch(() => this.confirmModal.pending = false)
        } else {
          this.markAsReadyForPricing();
        }
      }
      let warning = "";
      if (workingFromDraft) {
        warning = `You are currently working on an unsaved draft.<br/>
          <strong>Marking this estimation as ready for pricing will automatically save
          the current draft.</strong><br/>`
      }
      this.confirmModal.title = "Confirm Ready for Pricing"
      this.confirmModal.message = `${warning}Are you sure that this estimation is ready for pricing?`
      this.confirmModal.state = true;
    },
    markAsReadyForPricing() {
      this.updateEstimation({
        estimationId: this.estimation.id,
        data: { 'is_ready_for_pricing': true }
      }).then(() => {
        this.confirmModal.state = false;
        this.confirmModal.pending = false;
        this.spreadsheet.updateTable(
          function (el, cell, x) { 
            cell.classList.add('readonly');
          }
        )
      }).catch(() => this.confirmModal.pending = false)
    },
    confirmCompleted() {
      this.confirmModal.confirmCb = () => {
        this.confirmModal.pending = true;
        this.updateEstimation({
          estimationId: this.estimation.id,
          data: { 'is_completed': true }
        }).then(() => {
          this.confirmModal.state = false;
          this.confirmModal.pending = false;
        }).catch(() => this.confirmModal.pending = false)
      }
      this.confirmModal.title = "Confirm Estimation Completion"
      this.confirmModal.message = "Are you sure that this estimation is completed? \
        No further changes may be made after an estimation is marked as complete."
      this.confirmModal.state = true;
    },
    // confirmSalesApproval() {
    //   this.confirmModal.confirmCb = () => {
    //     this.updateEstimation({
    //       estimationId: this.estimation.id,
    //       data: { 'is_sales_approved': true }
    //     }).then(() => {
    //       this.confirmModal.state = false;
    //     })
    //   }
    //   this.confirmModal.title = "Confirm Sales Approval"
    //   this.confirmModal.message = "Are you sure you want to approve this estimation?"
    //   this.confirmModal.state = true;
    // },
    confirmClientApproval() {
      this.confirmModal.confirmCb = () => {
        this.confirmModal.pending = true;
        this.saveChanges().then(() => {
          this.updateEstimation({
            estimationId: this.estimation.id,
            data: { 'is_client_approved': true }
          }).then(() => {
            this.confirmModal.state = false;
            this.confirmModal.pending = false;
          }).catch(() => this.confirmModal.pending = false)
        }).catch(() => this.confirmModal.pending = false)
      }
      this.confirmModal.title = "Confirm Client Approval"
      this.confirmModal.message = "Are you sure you want to mark this estimation as \
      approved by the client? No further revisions can be made after client approval. \
      Note: Any unsaved changes will be automatically saved."
      this.confirmModal.state = true;
    },
    setSaving(v) {
      this.saving = v;
    },
    saveChanges() {
      this.saving = true;

      if (this.estimation.rfq.rfq_type !== CONDENSED) {
        this.$refs.fullEstimation.saveFullEstimation();
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/flexbox.scss';
@import '@/styles/table.scss';

.estimation__info {
  width: 100%;
  margin: 0em;
}
</style>